// Universal lens component (moved from Center/Home/OsintMap in Phase 9 — see
// GS_GRAPH_DESIGN.md §12). Consumed by the MAP (4 selectors) and the GS GRAPH
// (event types + regions); change with both pages in mind. Icon/status helpers
// still live in the OsintMap folder.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  Pencil,
  Search,
  Settings,
  Trash2,
  Waypoints,
} from "lucide-react";

import { statusTheme } from "../../Center/Home/OsintMap/constants";
import { selectMarkerIcon } from "../../Center/Home/OsintMap/sourcePresentation";
import { getMapEventIconSvgDataUrl } from "../../Center/Home/OsintMap/MapEventIcons";

const selectorButtonClass = (open) =>
  [
    "flex h-8 min-w-0 w-full max-w-full items-center justify-between gap-1.5 overflow-hidden rounded-lg border px-2.5 py-1.5 text-xs transition-all duration-200",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
    open
      ? "border-velBlue/40 bg-mainButtonBg/55 text-mainText shadow-[0_0_14px_rgba(14,165,233,0.14)]"
      : "border-mainDivider bg-mainButtonBg/30 text-mutedText hover:border-velBlue/30 hover:bg-mainButtonBg/55 hover:text-mainText",
  ].join(" ");

const dropdownPanelClass =
  "absolute left-0 top-full z-[1400] mt-2 w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-mainDivider bg-mainBg/95 shadow-[0_0_0_1px_rgba(14,165,233,0.06),0_0_22px_rgba(14,165,233,0.12),0_22px_48px_rgba(0,0,0,0.42)] backdrop-blur-md";

const smallActionClass =
  "rounded-md px-2 py-1 text-[10px] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60";

const formatSourceCount = (count) => {
  const numeric = Number(count || 0);
  if (!Number.isFinite(numeric)) return "0";
  if (numeric >= 1000) return `${Math.round(numeric / 100) / 10}k`;
  return String(numeric);
};

const isRegionalPostSource = (source = {}) =>
  Boolean(source.environmentRegion) ||
  String(source.id || "").startsWith("environment:");

const sourceIconProperties = (source = {}) => ({
  id: source.id,
  category: source.category,
  source: source.id,
  sourceLabel: source.label,
  title: source.label,
  feedType: source.feedType,
  environmentRegion: source.environmentRegion,
  iconType: isRegionalPostSource(source) ? "regional-post" : source.iconType,
  tags: [
    source.id,
    source.label,
    source.category,
    source.feedType,
    source.environmentRegion,
    isRegionalPostSource(source) ? "regional-post" : null,
  ].filter(Boolean),
});

function SourceRowIcon({ source, selected = true }) {
  // Feed-view options carry the exact icon + color the map canvas renders
  // (getFeedViews -> MapEventIcons). Reusing that SVG keeps the dropdown
  // visually identical to the markers. Sources without an explicit icon
  // (other selector instances) fall back to the legacy tag-based picker.
  const iconDataUrl = source.icon
    ? getMapEventIconSvgDataUrl(
        source.icon,
        source.color || "rgba(14,165,233,0.85)",
      )
    : null;

  if (iconDataUrl) {
    return (
      <span
        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-all duration-150 ${
          selected
            ? "border-mainDivider bg-mainButtonBg/35"
            : "border-mainDivider/60 bg-mainButtonBg/20"
        }`}
      >
        <img
          src={iconDataUrl}
          alt=""
          aria-hidden
          draggable={false}
          className={`h-[13px] w-[13px] transition-all duration-150 ${
            selected ? "" : "opacity-40 grayscale"
          }`}
        />
      </span>
    );
  }

  const Icon = selectMarkerIcon(sourceIconProperties(source));

  return (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-mainDivider bg-mainButtonBg/35 transition-all duration-150 ${
        selected ? "text-velBlue/90" : "text-mutedText/50"
      }`}
    >
      <Icon size={13} strokeWidth={2.25} />
    </span>
  );
}

export default function SourceSelector({
  allLabel = "All sources",
  emptyLabel = "No sources",
  countsReady = true,
  // For a dimension where an EMPTY selection means UNFILTERED (the lens core:
  // event types, regions), empty is not "nothing selected" — it is everything.
  // Rows render checked and the badge reads N/N, so collapsing "All" to the
  // empty set cannot read as a UI that just blanked itself. A toggle then
  // starts from the whole vocabulary, not from the empty list.
  emptyMeansAll = false,
  // Optional predicate naming rows that are NOT part of this dimension — the
  // map's favorited/custom feed views ride the event-type dropdown as PAGE
  // rows (§43.2). Their presence is what makes `Clear` a distinct action under
  // `emptyMeansAll`: it drops that half, which `All` selects. A list that is
  // entirely one dimension has no such half, so Clear would write exactly what
  // All writes and paint the same rows.
  isPageRow,
  // onChange(nextIds, intent) — `intent` names the ACTION the user took
  // ({action: "all" | "clear" | "toggle", id, selected}) so a consumer can
  // honour the intent instead of re-deriving it from a diff against a list
  // this component happens to be displaying. Optional: consumers that only
  // need the ids ignore it.
  onChange,
  selectedSources = [],
  sourceCounts = null,
  sourceSettingDefinitions = {},
  sourceSettings = {},
  sourceStatuses = {},
  sources = [],
  summaryLabel = "sources",
  title = "Sources",
  onSourceSettingsChange,
  // When provided, filtering is delegated to the backend: the query is reported here
  // (parent fetches + supplies pre-filtered `sources`) and the local filter is bypassed.
  onSearchChange,
  // Sources whose id is in this set get a trash button that calls onDeleteSource — used
  // for user-owned entries (e.g. custom regions) that can be permanently removed.
  deletableIds,
  onDeleteSource,
  // Sources whose id is in this set get a pencil button that calls onEditSource —
  // used to redraw a custom region in place. Closes the dropdown on click.
  editableIds,
  onEditSource,
  // Optional per-row icon override: (source, selected) => node. Lets pages
  // whose sources aren't map feed-views (e.g. the graph's entity types)
  // supply their own swatch/icon while keeping the row layout identical.
  renderRowIcon,
  // When provided, renders a "draw a custom region" action at the top of the panel;
  // clicking it closes the dropdown and hands off to the parent (lasso tool).
  onDrawCustom,
  drawLabel = "Draw a custom region",
}) {
  const [open, setOpen] = useState(false);
  const [expandedSourceId, setExpandedSourceId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    if (onSearchChange) onSearchChange(searchQuery);
  }, [searchQuery, onSearchChange]);

  // A closed dropdown keeps no query: reopening must show the full list, not
  // the slice the last visit filtered to (and, with server-driven filtering,
  // must not leave the parent holding a search-scoped `sources`).
  useEffect(() => {
    if (!open) setSearchQuery("");
  }, [open]);

  const visibleSources = useMemo(() => {
    // Server-driven search: `sources` is already filtered by the parent, show as-is.
    if (onSearchChange) return sources;

    const query = searchQuery.trim().toLowerCase();
    if (!query) return sources;

    return sources.filter((source) =>
      [
        source.id,
        source.label,
        source.category,
        source.feedType,
        source.environmentRegion,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [onSearchChange, searchQuery, sources]);

  const selectedSet = useMemo(
    () => new Set(selectedSources),
    [selectedSources],
  );

  const allEnabledSources = useMemo(
    () => sources.filter((source) => source.enabled !== false),
    [sources],
  );

  const visibleEnabledSources = useMemo(
    () => visibleSources.filter((source) => source.enabled !== false),
    [visibleSources],
  );

  // The unfiltered state, presented as "everything is on" (see emptyMeansAll).
  const showAllSelected = emptyMeansAll && selectedSources.length === 0;

  const hasPageRows = useMemo(
    () => (isPageRow ? sources.some((source) => isPageRow(source)) : false),
    [isPageRow, sources],
  );

  const allSelected =
    showAllSelected ||
    (allEnabledSources.length > 0 &&
      allEnabledSources.every((source) => selectedSet.has(source.id)));

  const isRowSelected = (sourceId) =>
    showAllSelected || selectedSet.has(sourceId);

  const selectedLabels = sources
    .filter((source) => selectedSet.has(source.id))
    .map((source) => source.label);

  const totalSourceCount = allEnabledSources.length || sources.length;

  // Keep the closed-state label/count stable while map data is refreshing.
  // The selected side still updates immediately when the user checks/unchecks items,
  // but we do not collapse to the old "—/—" loading placeholder.
  const label = allSelected
    ? allLabel
    : selectedLabels.length === 0
      ? emptyLabel
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${selectedLabels.length} ${summaryLabel}`;

  const selectedCountLabel = showAllSelected
    ? `${totalSourceCount}/${totalSourceCount}`
    : `${selectedLabels.length}/${totalSourceCount}`;

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const setAllSources = () => {
    // The id list is the FALLBACK: `sources` may be a search-scoped slice
    // (server-driven filtering), so a consumer that can express "unfiltered"
    // must read the intent instead of trusting this enumeration.
    onChange?.(
      allEnabledSources.map((source) => source.id),
      { action: "all" },
    );
  };

  const clearSources = () => {
    onChange?.([], { action: "clear" });
  };

  const toggleSource = (sourceId) => {
    // Leaving the unfiltered state by enumeration is only honest when this
    // component HOLDS the whole vocabulary. With server-driven filtering
    // `sources` is a search-scoped slice, so "everything except this" would
    // silently mean "everything currently matching" (and for regions it would
    // enumerate ~300 countries into a 60-wide server cap): there the first row
    // click starts a fresh selection instead.
    if (showAllSelected && onSearchChange) {
      onChange?.([sourceId], { action: "toggle", id: sourceId, selected: true });
      return;
    }
    const wasSelected = isRowSelected(sourceId);
    // Under emptyMeansAll every row reads as checked, so the base a toggle
    // acts on is the whole vocabulary — unchecking one row means "everything
    // except this", not "only this".
    const base = showAllSelected
      ? allEnabledSources.map((source) => source.id)
      : selectedSources;
    onChange?.(
      wasSelected
        ? base.filter((id) => id !== sourceId)
        : [...base, sourceId],
      { action: "toggle", id: sourceId, selected: !wasSelected },
    );
  };

  const settingForSource = (sourceId) => {
    const definition = sourceSettingDefinitions[sourceId];
    const setting = sourceSettings[sourceId] || {};
    if (!definition) return null;

    return {
      density: setting.density || definition.density || "medium",
      minZoom: Number(setting.minZoom ?? definition.minZoom?.default),
      maxFeatures: Number(
        setting.maxFeatures ?? definition.maxFeatures?.default,
      ),
    };
  };

  const updateSourceSetting = (sourceId, patch) => {
    const definition = sourceSettingDefinitions[sourceId];
    if (!definition || !onSourceSettingsChange) return;

    const current = settingForSource(sourceId) || {};
    onSourceSettingsChange({
      ...sourceSettings,
      [sourceId]: { ...current, ...patch },
    });
  };

  const applyDensity = (sourceId, density) => {
    const preset = sourceSettingDefinitions[sourceId]?.presets?.[density] || {};
    updateSourceSetting(sourceId, { density, ...preset });
  };

  const resetSourceSetting = (sourceId) => {
    if (!onSourceSettingsChange) return;

    const nextSettings = { ...sourceSettings };
    delete nextSettings[sourceId];
    onSourceSettingsChange(nextSettings);
  };

  return (
    <div
      ref={rootRef}
      className={`relative min-w-0 max-w-full flex-1 basis-0 overflow-visible ${
        open ? "z-[1400]" : "z-[1]"
      }`}
    >
      <button
        type="button"
        aria-expanded={open}
        className={selectorButtonClass(open)}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          <span className="min-w-0 flex-1 truncate text-left">{label}</span>
          <span className="shrink-0 rounded-md border border-mainDivider bg-mainButtonBg/40 px-1.5 text-[10px] text-mutedText">
            {selectedCountLabel}
          </span>
        </span>

        <ChevronDown
          size={14}
          className={`shrink-0 text-mutedText transition-transform duration-200 ${
            open ? "rotate-180 text-velBlue" : ""
          }`}
        />
      </button>

      {open && (
        <div className={dropdownPanelClass}>
          <div className="flex items-center justify-between border-b border-mainDivider px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-mutedText">
              {title}
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                className={`${smallActionClass} text-velBlue hover:bg-velBlue/10`}
                onClick={setAllSources}
              >
                All
              </button>

              {/* Two buttons must not be one action. Where empty means
                  UNFILTERED, `Clear` writes the empty set and every row paints
                  checked — exactly what `All` does — so on a list that is
                  entirely this dimension only `All` is offered. The exception
                  is a list carrying PAGE rows (`isPageRow`): there Clear drops
                  that half and All re-selects it, so both are real. Selectors
                  where empty really does mean nothing always show both, and
                  nobody offers Clear from the unfiltered state. */}
              {!showAllSelected && (!emptyMeansAll || hasPageRows) && (
                <button
                  type="button"
                  className={`${smallActionClass} text-mutedText hover:bg-mainButtonBg/60 hover:text-mainText`}
                  onClick={clearSources}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {onDrawCustom && (
            <button
              type="button"
              className="flex w-full items-center gap-2 border-b border-mainDivider px-3 py-2.5 text-xs text-velBlue transition-colors hover:bg-velBlue/10"
              onClick={() => {
                setOpen(false);
                onDrawCustom();
              }}
            >
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-velBlue/30 bg-velBlue/10">
                <Waypoints size={13} />
              </span>
              <span className="min-w-0 flex-1 text-left">{drawLabel}</span>
            </button>
          )}

          <div className="border-b border-mainDivider p-2">
            <label className="flex items-center gap-2 rounded-lg border border-mainDivider bg-mainButtonBg/30 px-2.5 py-1.5 text-xs text-mutedText focus-within:border-velBlue/35 focus-within:text-mainText">
              <Search size={13} className="shrink-0 text-velBlue/70" />
              <input
                className="min-w-0 flex-1 bg-transparent text-xs text-mainText outline-none placeholder:text-mutedText/70"
                placeholder={`Search ${summaryLabel}...`}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </label>
          </div>

          <div className="max-h-80 overflow-auto p-1.5">
            {visibleSources.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-mutedText">
                No matching {summaryLabel}
              </div>
            )}

            {visibleSources.map((source) => {
              const selected = isRowSelected(source.id);
              const pointCount = sourceCounts
                ? Number(sourceCounts[source.id] ?? 0)
                : null;
              const status = sourceStatuses[source.id];
              const definition = sourceSettingDefinitions[source.id];
              const setting = settingForSource(source.id);
              const expanded = expandedSourceId === source.id;
              const settingsId = `source-settings-${source.id}`;

              return (
                <div
                  key={source.id}
                  className={`group overflow-hidden rounded-lg transition-colors duration-150 ${
                    selected
                      ? "text-mainText"
                      : "text-mutedText hover:bg-mainButtonBg/35 hover:text-mainText"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-2 text-left text-xs"
                      onClick={() => toggleSource(source.id)}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          selected
                            ? "border-velBlue/60 bg-velBlue/20 text-velBlue"
                            : "border-mainDivider bg-mainButtonBg/30 text-transparent"
                        }`}
                      >
                        <Check size={11} />
                      </span>

                      {renderRowIcon ? (
                        renderRowIcon(source, selected)
                      ) : (
                        <SourceRowIcon source={source} selected={selected} />
                      )}

                      <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                          statusTheme[status?.status] || "bg-slate-500"
                        }`}
                      />

                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className="min-w-0 truncate">
                            {source.label}
                          </span>
                          {source.feedViewKind === "custom" && (
                            <span className="shrink-0 rounded border border-mainDivider bg-mainButtonBg/45 px-1 py-px text-[8px] uppercase leading-none tracking-[0.14em] text-mutedText/80">
                              custom
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-[10px] text-mutedText/75">
                          {source.category || source.feedType || "OSINT"}
                        </span>
                      </span>

                      {pointCount !== null && (
                        <span
                          className={`ml-1 shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-medium tabular-nums transition-colors duration-150 ${
                            selected && pointCount > 0
                              ? "bg-velBlue/12 text-velBlue/90"
                              : "bg-mainButtonBg/40 text-mutedText/70"
                          }`}
                          title={`${pointCount} ${
                            pointCount === 1 ? "point" : "points"
                          } on the map`}
                        >
                          {formatSourceCount(pointCount)}
                        </span>
                      )}
                    </button>

                    {definition && (
                      <button
                        type="button"
                        aria-controls={settingsId}
                        aria-expanded={expanded}
                        aria-label={`Configure ${source.label}`}
                        className={`mr-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 ${
                          expanded
                            ? "border-velBlue/45 bg-velBlue/15 text-velBlue"
                            : "border-mainDivider bg-mainButtonBg/35 text-mutedText hover:border-velBlue/30 hover:text-mainText"
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setExpandedSourceId(expanded ? null : source.id);
                        }}
                      >
                        <Settings size={12} />
                      </button>
                    )}

                    {onEditSource && editableIds?.has(source.id) && (
                      <button
                        type="button"
                        aria-label={`Redraw ${source.label}`}
                        title="Redraw"
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-mainDivider bg-mainButtonBg/35 text-mutedText transition-all duration-200 hover:border-velBlue/40 hover:text-velBlue focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpen(false);
                          onEditSource(source.id);
                        }}
                      >
                        <Pencil size={12} />
                      </button>
                    )}

                    {onDeleteSource && deletableIds?.has(source.id) && (
                      <button
                        type="button"
                        aria-label={`Delete ${source.label}`}
                        title="Delete"
                        className="mr-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-mainDivider bg-mainButtonBg/35 text-mutedText transition-all duration-200 hover:border-red-400/40 hover:text-red-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                        onClick={(event) => {
                          event.stopPropagation();
                          onDeleteSource(source.id);
                        }}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>

                  {definition && expanded && (
                    <div
                      id={settingsId}
                      className="mx-2 mb-2 rounded-lg border border-mainDivider bg-mainButtonBg/25 p-2"
                    >
                      {definition.presets && (
                        <div className="mb-2">
                          <div className="mb-1 text-[9px] uppercase tracking-[0.18em] text-mutedText">
                            Density
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {Object.keys(definition.presets).map((density) => (
                              <button
                                key={density}
                                type="button"
                                className={`${smallActionClass} ${
                                  setting?.density === density
                                    ? "bg-velBlue/15 text-velBlue"
                                    : "text-mutedText hover:bg-mainButtonBg/60 hover:text-mainText"
                                }`}
                                onClick={() => applyDensity(source.id, density)}
                              >
                                {density}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {definition.minZoom && (
                        <label className="mb-2 block">
                          <span className="mb-1 block text-[9px] uppercase tracking-[0.18em] text-mutedText">
                            Min zoom
                          </span>
                          <input
                            type="number"
                            className="w-full rounded-md border border-mainDivider bg-mainButtonBg/35 px-2 py-1 text-xs text-mainText outline-none focus:border-velBlue/40"
                            value={
                              Number.isFinite(setting?.minZoom)
                                ? setting.minZoom
                                : ""
                            }
                            min={definition.minZoom.min}
                            max={definition.minZoom.max}
                            onChange={(event) =>
                              updateSourceSetting(source.id, {
                                minZoom: Number(event.target.value),
                              })
                            }
                          />
                        </label>
                      )}

                      {definition.maxFeatures && (
                        <label className="mb-2 block">
                          <span className="mb-1 block text-[9px] uppercase tracking-[0.18em] text-mutedText">
                            Max features
                          </span>
                          <input
                            type="number"
                            className="w-full rounded-md border border-mainDivider bg-mainButtonBg/35 px-2 py-1 text-xs text-mainText outline-none focus:border-velBlue/40"
                            value={
                              Number.isFinite(setting?.maxFeatures)
                                ? setting.maxFeatures
                                : ""
                            }
                            min={definition.maxFeatures.min}
                            max={definition.maxFeatures.max}
                            onChange={(event) =>
                              updateSourceSetting(source.id, {
                                maxFeatures: Number(event.target.value),
                              })
                            }
                          />
                        </label>
                      )}

                      <button
                        type="button"
                        className={`${smallActionClass} text-mutedText hover:bg-mainButtonBg/60 hover:text-mainText`}
                        onClick={() => resetSourceSetting(source.id)}
                      >
                        Reset
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {visibleSources.length > 0 && searchQuery.trim() && (
              <div className="border-t border-mainDivider/70 px-3 py-2 text-[10px] text-mutedText">
                Showing {visibleEnabledSources.length} matching {summaryLabel}.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
